/**
 * ActivityFeed — live ticker of task status transitions on the dashboard.
 *
 * Source is `task_events`, the append-only log the log_task_event trigger has been
 * writing on every status change since August. We read it directly instead of going
 * through the store: subscribeToChanges re-fetches the WHOLE store on any change,
 * which is far too heavy for a ticker, and the feed only ever needs new rows.
 *
 * task_events records what changed and when, but not who — adding the actor needs a
 * schema change on the write path, so for now the feed reads "Task → Status".
 */
import React, { useEffect, useRef, useState } from 'react';
import { Card } from './UI';
import { fetchRecentActivity, subscribeToActivity } from '../utils/db';

// Matches the task board's status palette so a state reads the same everywhere.
const TONE = {
  'Not Started':      { c: 'var(--text3)', bg: 'rgba(239,235,228,0.07)' },
  'In Progress':      { c: '#8FA3B0',      bg: 'rgba(143,163,176,0.13)' },
  'Changes Required': { c: '#C08079',      bg: 'rgba(192,128,121,0.13)' },
  'Ready for Review': { c: '#C79A5B',      bg: 'rgba(199,154,91,0.13)'  },
  'Client Review':    { c: 'var(--accent)', bg: 'rgba(169,137,83,0.15)' },
  'Reviewed':         { c: '#8FB08A',      bg: 'rgba(143,176,138,0.13)' },
  'Completed':        { c: '#8FB08A',      bg: 'rgba(143,176,138,0.13)' },
};
const tone = s => TONE[s] || TONE['Not Started'];

// Plain-language verb for the transition, so the row reads as something that happened
// rather than as a state machine edge.
function verb(oldStatus, newStatus) {
  if (!oldStatus)                        return 'added';
  switch (newStatus) {
    case 'In Progress':      return 'started';
    case 'Ready for Review': return 'submitted for review';
    case 'Client Review':    return 'sent to the client';
    case 'Reviewed':
    case 'Completed':        return 'approved';
    case 'Changes Required': return 'sent back for changes';
    case 'Not Started':      return 'reset';
    default:                 return 'moved';
  }
}

function ago(ts) {
  const secs = Math.max(0, (Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 45)      return 'just now';
  if (secs < 3600)    return Math.round(secs / 60) + 'm ago';
  if (secs < 86400)   return Math.round(secs / 3600) + 'h ago';
  if (secs < 172800)  return 'yesterday';
  return Math.round(secs / 86400) + 'd ago';
}

export default function ActivityFeed({ limit = 12 }) {
  const [events, setEvents] = useState(null);   // null = still loading
  const [live, setLive]     = useState(false);
  const [, setTick]         = useState(0);      // re-render so "2m ago" stays honest
  const seen = useRef(new Set());

  useEffect(() => {
    let cancelled = false;

    fetchRecentActivity(40).then(rows => {
      if (cancelled) return;
      rows.forEach(r => seen.current.add(r.id));
      setEvents(rows);
    });

    // Guard against the same row arriving twice (an INSERT that races the initial
    // fetch would otherwise appear in both).
    const unsub = subscribeToActivity(row => {
      if (cancelled || seen.current.has(row.id)) return;
      seen.current.add(row.id);
      setEvents(prev => [row, ...(prev || [])].slice(0, 40));
      setLive(true);
    });

    const t = setInterval(() => setTick(n => n + 1), 60000);
    return () => { cancelled = true; clearInterval(t); unsub(); };
  }, []);

  const rows = (events || []).slice(0, limit);

  return (
    <Card>
      <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span style={{ fontFamily: 'var(--serif)', fontSize: 18, color: 'var(--text)' }}>Activity</span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--text3)' }}>
          Task board
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.13em', textTransform: 'uppercase', color: live ? 'var(--accent)' : 'var(--text3)' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: live ? 'var(--accent)' : 'var(--text3)' }} />
          Live
        </span>
      </div>

      {events === null ? (
        <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text3)', fontSize: 14 }}>Loading activity…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text3)', fontSize: 14 }}>No task activity yet.</div>
      ) : rows.map((e, i) => {
        const t = tone(e.new_status);
        return (
          <div key={e.id} className="feed-item" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 22px', borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.c, flexShrink: 0 }} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.task_name || 'Untitled task'}
                <span style={{ color: 'var(--text3)' }}> — {verb(e.old_status, e.new_status)}</span>
              </div>
              {e.client_name && (
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text3)', marginTop: 3 }}>
                  {e.client_name}
                </div>
              )}
            </div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '4px 8px', borderRadius: 'var(--radius)', background: t.bg, color: t.c, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {e.new_status}
            </span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--text3)', whiteSpace: 'nowrap', flexShrink: 0, minWidth: 62, textAlign: 'right' }}>
              {ago(e.changed_at)}
            </span>
          </div>
        );
      })}
    </Card>
  );
}
